"use client"

import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import Dialog from "@mui/material/Dialog"
import Image from "next/image"
import { useCallback, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import { FreeMode } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import { useIsDesktop } from "../../theme"
import GradientChevron from "../../icons/GradientChevron"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import PlayIcon from "../../icons/PlayIcon"
import PauseIcon from "../../icons/PauseIcon"
import { useTransitionUpStyles as transitionUpStyles } from "../../transitions/useTransitionUpStyles"
import { useInViewConfig, delayTime } from "../../transitions/utils"
import {
  ChevronWrapper,
  CtaButton,
  CtaButtonExternal,
  CtaSection,
  FeatureIcon,
  FeatureLabel,
  FeatureTag,
  FeaturesGrid,
  GalleryImage,
  GalleryImageMobile,
  GalleryItem,
  GalleryNativeVideo,
  GallerySwiperContainer,
  GalleryVideo,
  LightboxArrowButton,
  LightboxCloseButton,
  LightboxContent,
  LightboxCounter,
  LightboxImageWrapper,
  LightboxNav,
  OuterRoot,
  PriceAmount,
  PriceLabel,
  PricePrefix,
  PriceRow,
  PriceSection,
  RichDescription,
  Root,
  Title,
  TitleRow,
  VideoToggle,
} from "./styles"

type VehicleFleetGalleryData =
  components["schemas"]["VehicleFleetGalleryPropertiesModel"]
// EGDE-601: the fleet gallery has its own category element. It used to share
// `galleryCategoryItem` with the Image Gallery, which meant an image-gallery
// editor was shown this component's vehicle description / attributes / price /
// CTA fields. The two are now separate element types, and the per-vehicle-type
// fields added by EGDE-424 are part of this one's generated model — the local
// override that stood in for them is no longer needed.
type GalleryCategoryData =
  components["schemas"]["VehicleFleetCategoryItemPropertiesModel"]
// EGDE-718 (r2): the gallery item got the same split as the category above —
// it used to share `galleryImageItem` with the Image Gallery, so the Image
// Title field added there was shown to fleet editors too. The fleet now has
// its own `vehicleGalleryItem` element (no title field).
type GalleryImageData =
  components["schemas"]["VehicleGalleryItemPropertiesModel"]
type VehicleFeatureTagData =
  components["schemas"]["VehicleFeatureTagPropertiesModel"]

interface Props {
  data: VehicleFleetGalleryData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

type EmbeddedVideoData = components["schemas"]["EmbeddedVideoPropertiesModel"]

/**
 * EGDE-633: one resolved media slot. Both the desktop field and the mobile
 * override accept an image OR an uploaded video, so each slot has to carry its
 * own medium — previously only the desktop field was classified and the mobile
 * override was assumed to be an image, which handed an editor-supplied mp4 to
 * next/image and rendered the slide as a broken picture.
 */
type GalleryAsset = { url: string; isVideo: boolean }

type GalleryMediaResult =
  | { type: "youtubeVideo"; youtubeUrl: string; title: string }
  | {
      type: "media"
      desktop: GalleryAsset | null
      /** EGDE-468: optional portrait crop from the responsiveImage element. */
      mobile: GalleryAsset | null
      alt: string
    }

function extractVideoId(input?: string | null): string | null {
  if (!input) return null
  const trimmed = input.trim()
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed
  try {
    const url = new URL(trimmed)
    if (url.searchParams.has("v")) return url.searchParams.get("v")
    if (url.hostname === "youtu.be") return url.pathname.slice(1) || null
    const embedMatch = url.pathname.match(/\/embed\/([\w-]+)/)
    if (embedMatch) return embedMatch[1]
    const vMatch = url.pathname.match(/\/v\/([\w-]+)/)
    if (vMatch) return vMatch[1]
  } catch {
    /* not a valid URL */
  }
  return null
}

function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as Record<string, unknown>
  const mediaType = String(media?.mediaType ?? "").toLowerCase()
  const extension = String(media?.extension ?? "").toLowerCase()
  // EGDE-566/571: Umbraco reports "umbracoMediaVideo" (with empty extension)
  // for uploaded videos — strict equality never matched (cf. EGDE-36).
  return (
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)
  )
}

/**
 * EGDE-633: resolve one media picker into a URL plus its medium. Applied to the
 * desktop field and the mobile override alike, so the two can differ in medium
 * (a still desktop photo with a mobile video clip, or the reverse).
 */
function resolveAsset(mediaPicker: unknown): GalleryAsset | null {
  const rel = getMediaUrl(mediaPicker)
  if (!rel) return null
  return { url: getUmbracoMediaUrl(rel), isVideo: isVideoMedia(mediaPicker) }
}

const EMPTY_MEDIA: GalleryMediaResult = {
  type: "media",
  desktop: null,
  mobile: null,
  alt: "",
}

function getGalleryMedia(blockList: unknown): GalleryMediaResult {
  const list = blockList as
    | {
        items?: Array<{
          content: {
            contentType?: string
            properties?: Record<string, unknown>
          }
        }>
      }
    | undefined
  const item = list?.items?.[0]
  if (!item) return EMPTY_MEDIA

  const contentType = item.content?.contentType
  const props = item.content?.properties

  if (contentType === "embeddedVideo" && props) {
    const videoProps = props as unknown as EmbeddedVideoData
    return {
      type: "youtubeVideo",
      youtubeUrl: videoProps.youtubeUrl ?? "",
      title: videoProps.title ?? "Video",
    }
  }

  if (!props) return EMPTY_MEDIA

  // EGDE-468: honour the responsiveImage element's mobile crop — it was
  // uploaded by editors but never read, so mobile always showed the desktop
  // image. EGDE-633: it is read for a video desktop field too — the old
  // early-return for uploaded video dropped the override on the floor.
  return {
    type: "media",
    desktop: resolveAsset(props.desktopImage),
    mobile: resolveAsset(props.mobileImage),
    alt: (props.altText as string) ?? "",
  }
}

/**
 * EGDE-633: the asset each breakpoint shows. The mobile field is an *override* —
 * when there is no desktop asset the mobile one simply serves every breakpoint,
 * so there is nothing to toggle and only one slot renders (which is also the
 * pre-EGDE-633 DOM for every item that has no mobile crop).
 */
function getSlots(media: GalleryMediaResult): {
  desktop: GalleryAsset | null
  mobile: GalleryAsset | null
} {
  if (media.type !== "media") return { desktop: null, mobile: null }
  return {
    desktop: media.desktop ?? media.mobile,
    mobile: media.desktop ? media.mobile : null,
  }
}

export default function VehicleFleetGallery({ data, culture }: Props) {
  // EGDE-424: the first tab is a real category, NOT an "All" tab. Selection
  // starts on the first populated category and each tab shows only its own
  // vehicle type's media (no merged "All" view). null = fall back to first.
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const isRtl = culture === "he-IL" || culture === "ar"

  // EGDE-508: the EGDE-318 scroll-scrubbed drift is gone — QA: "disable the
  // mouse scroll interaction, there should be only an entrance transition
  // here". EGDE-510: that entrance is the standard one-shot rise-in — title
  // and tabs first, then the image row, then the per-type content below.
  const { ref: entranceRef, inView } = useInView(useInViewConfig)

  const categoryItems = data.categories?.items ?? []
  const categories = categoryItems.map((block) => {
    const props = block.content.properties as GalleryCategoryData | undefined
    return {
      key: props?.categoryKey ?? "",
      label: props?.label ?? "",
      // EGDE-424: per-vehicle-type content (all optional — the frontend falls
      // back to the block-level fields so existing content keeps rendering).
      descriptionMarkup: props?.description?.markup ?? null,
      featureTags: props?.featureTags,
      priceLabel: props?.priceLabel ?? null,
      pricePrefix: props?.pricePrefix ?? null,
      priceAmount: props?.priceAmount ?? null,
      ctaLabel: props?.ctaLabel ?? null,
      ctaUrl: props?.ctaUrl,
    }
  })

  const allGalleryItems = data.galleryItems?.items ?? []
  const galleryImages = allGalleryItems
    .filter((block) => {
      const props = block.content.properties as GalleryImageData | undefined
      // Hide only when the visibility toggle is explicitly false.
      // Umbraco may return null/undefined for an untouched toggle, which must
      // still be treated as visible. (EGDE-70: a single "Is Visible" toggle.)
      return props?.isVisible !== false
    })
    .map((block, index) => {
      const props = block.content.properties as GalleryImageData | undefined
      const media = getGalleryMedia(props?.image)
      return {
        id: block.content.id ?? `gallery-${index}`,
        media,
        categoryKey: props?.categoryKey ?? "",
      }
    })
    // EGDE-575: an item whose media element is empty (no image/video actually
    // attached) must not render as a blank slide. EGDE-633: the desktop field is
    // mandatory in the CMS, but an item carrying only a mobile override is still
    // renderable — it just has one asset for every breakpoint.
    .filter((img) =>
      img.media.type === "youtubeVideo"
        ? Boolean(img.media.youtubeUrl)
        : Boolean(img.media.desktop ?? img.media.mobile),
    )

  // ESEIM-234: filter out categories that have no items
  const populatedCategories = categories.filter((cat) =>
    galleryImages.some((img) => img.categoryKey === cat.key),
  )

  // ESEIM-231: hide tab bar when only 1 category (or none)
  const showTabs = populatedCategories.length > 1

  // EGDE-424: no synthetic "All" tab — default to the first populated category.
  const selectedCategory =
    activeCategory !== null &&
    populatedCategories.some((cat) => cat.key === activeCategory)
      ? activeCategory
      : (populatedCategories[0]?.key ?? "")

  const filteredImages = galleryImages.filter(
    (img) => img.categoryKey === selectedCategory,
  )

  // EGDE-424: everything below the gallery is per-vehicle-type. Each category
  // carries its own description / attributes / price text / CTA; the old
  // block-level fields remain as a fallback so pre-existing content (and a
  // category the editor hasn't filled yet) still renders instead of vanishing.
  const selectedCategoryData = categories.find(
    (cat) => cat.key === selectedCategory,
  )

  const parseFeatureTags = (blockList: unknown) => {
    const items =
      (
        blockList as {
          items?: Array<{ content: { properties?: Record<string, unknown> } }>
        }
      )?.items ?? []
    return items.map((block) => {
      const props = block.content.properties as
        | VehicleFeatureTagData
        | undefined
      const iconUrl = getMediaUrl(props?.icon as unknown)
      return {
        icon: iconUrl ? getUmbracoMediaUrl(iconUrl) : null,
        label: props?.label ?? "",
      }
    })
  }

  // EGDE-470: the old block-level fields (description / featureTags / price* /
  // cta*) were removed from the CMS — everything below the gallery is
  // per-vehicle-type now, with no block-level fallback.
  const featureTags = parseFeatureTags(selectedCategoryData?.featureTags)

  const rawDescriptionMarkup = selectedCategoryData?.descriptionMarkup ?? ""
  const descriptionMarkup =
    rawDescriptionMarkup && rawDescriptionMarkup.replace(/<[^>]*>/g, "").trim()
      ? replaceRichTextLinks(rawDescriptionMarkup)
      : null

  const priceLabel = selectedCategoryData?.priceLabel ?? null
  const pricePrefix = selectedCategoryData?.pricePrefix ?? null
  const priceAmount = selectedCategoryData?.priceAmount ?? null

  // The category CTA always opens in a new tab (per EGDE-424).
  const ctaResolved = resolveLinkHref(selectedCategoryData?.ctaUrl, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const ctaLabel = selectedCategoryData?.ctaLabel ?? null
  const ctaUrl = ctaLabel ? (ctaResolved?.href ?? null) : null
  const ctaLinkType = ctaResolved?.isInternal ? "Content" : undefined
  const ctaTarget = "_blank"

  /* ---- EGDE-574: in-place video playback ----
     The badge on a video slide is a real play/pause button. State is driven by
     the <video> element's own play/pause/ended events rather than set on click,
     so the glyph cannot drift out of sync with what the media is actually doing
     (a clip reaching its end flips back to "play" on its own).
     Playback stays muted — this is a preview inside a 384px card; the lightbox
     is where a video plays with sound and full native controls.
     EGDE-633: keyed per SLOT (`<id>-desktop` / `<id>-mobile`), not per slide —
     one card can now mount a desktop and a mobile <video>, and a single key
     would have made the two fight over the same ref and playing state. Only the
     slot for the current breakpoint is displayed, so only it is operable. */
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({})
  const [playingId, setPlayingId] = useState<string | null>(null)

  const pauseAllVideos = useCallback(() => {
    Object.values(videoRefs.current).forEach((el) => {
      if (el && !el.paused) el.pause()
    })
  }, [])

  const handleToggleVideo = useCallback(
    (id: string, event: React.MouseEvent) => {
      // The whole card opens the lightbox — this control must not trigger it.
      event.stopPropagation()
      const el = videoRefs.current[id]
      if (!el) return
      if (el.paused) {
        // Only one preview plays at a time.
        Object.entries(videoRefs.current).forEach(([key, other]) => {
          if (key !== id && other && !other.paused) other.pause()
        })
        void el.play().catch(() => {
          // Autoplay/permission refusal — leave the glyph on "play".
        })
      } else {
        el.pause()
      }
    },
    [],
  )

  /* EGDE-633: render one slot. A video slot carries its own play/pause control,
     so the badge always belongs to the media actually on screen — VideoToggle is
     absolutely positioned and both slot wrappers are inset:0 over the card, so
     it lands in the same corner it did when it was a child of the card itself. */
  const renderAsset = (
    asset: GalleryAsset,
    alt: string,
    slotId: string,
    sizes: string,
  ) =>
    asset.isVideo ? (
      <>
        <GalleryNativeVideo
          ref={(el) => {
            videoRefs.current[slotId] = el
          }}
          src={asset.url}
          muted
          playsInline
          preload="metadata"
          onPlay={() => setPlayingId(slotId)}
          onPause={() =>
            setPlayingId((prev) => (prev === slotId ? null : prev))
          }
          onEnded={() =>
            setPlayingId((prev) => (prev === slotId ? null : prev))
          }
        />
        {/* EGDE-574: play/pause the slide in place, and mark it as a video in
            the first place (Figma play badge). */}
        <VideoToggle
          type="button"
          aria-label={playingId === slotId ? "Pause video" : "Play video"}
          onClick={(e) => handleToggleVideo(slotId, e)}
        >
          {playingId === slotId ? (
            <PauseIcon width={11} height={12} />
          ) : (
            <PlayIcon width={12} height={14} />
          )}
        </VideoToggle>
      </>
    ) : (
      <Image
        src={asset.url}
        alt={alt}
        fill
        sizes={sizes}
        style={{ objectFit: "cover" }}
      />
    )

  /* EGDE-574: changing category unmounts the current slides. An unmounting
     <video> fires no `pause` event, so without this the element kept running
     detached and `playingId` stayed pointing at it: returning to the category
     showed a Pause glyph over a paused video, and the next click played instead
     of pausing. Pause here — while the elements are still mounted — and reset. */
  const handleCategoryChange = useCallback(
    (key: string) => {
      pauseAllVideos()
      setPlayingId(null)
      setActiveCategory(key)
    },
    [pauseAllVideos],
  )

  /* ---- Lightbox handlers ---- */
  const lightboxOpen = lightboxIndex !== null
  const handleOpenLightbox = useCallback(
    (index: number) => {
      // EGDE-574: don't leave a preview running behind the dialog.
      pauseAllVideos()
      setLightboxIndex(index)
    },
    [pauseAllVideos],
  )
  const handleCloseLightbox = useCallback(() => {
    setLightboxIndex(null)
  }, [])
  const handlePrevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))
  }, [])
  const handleNextImage = useCallback(() => {
    setLightboxIndex((prev) =>
      prev !== null && prev < filteredImages.length - 1 ? prev + 1 : prev,
    )
  }, [filteredImages.length])

  // RTL-aware arrow handlers
  const handleLeftArrow = isRtl ? handleNextImage : handlePrevImage
  const handleRightArrow = isRtl ? handlePrevImage : handleNextImage
  const leftDisabled =
    lightboxIndex === null ||
    (isRtl ? lightboxIndex >= filteredImages.length - 1 : lightboxIndex <= 0)
  const rightDisabled =
    lightboxIndex === null ||
    (isRtl ? lightboxIndex <= 0 : lightboxIndex >= filteredImages.length - 1)

  const currentLightboxImage =
    lightboxIndex !== null ? filteredImages[lightboxIndex] : null

  /* EGDE-633: the lightbox showed the DESKTOP asset at every viewport, so on
     mobile it contradicted the card it was opened from — with a mobile video
     override you tapped a clip and got a still photo. It now opens whatever the
     card is showing at this breakpoint (same `lg` boundary as the CSS toggle in
     styles.ts). The dialog only ever renders after a tap, so the media query has
     long since resolved — there is no server/hydration render to disagree with. */
  const isDesktop = useIsDesktop()
  const lightboxAsset = (() => {
    if (currentLightboxImage?.media.type !== "media") return null
    const { desktop, mobile } = getSlots(currentLightboxImage.media)
    return (isDesktop ? desktop : (mobile ?? desktop)) ?? null
  })()

  // EGDE-464: the CMS title is a rich text field — the Delivery API returns
  // { markup } instead of the plain string the old Textstring produced. Both
  // shapes render so pre-migration content keeps working.
  const rawTitle = data.title as unknown
  const rawTitleMarkup =
    rawTitle && typeof rawTitle === "object"
      ? ((rawTitle as { markup?: string | null }).markup ?? "")
      : ""
  // EGDE-476: like the description, title hyperlinks must be rewritten from
  // raw Umbraco hrefs to site routes — unrewritten they 404 on the frontend.
  const titleMarkup = rawTitleMarkup ? replaceRichTextLinks(rawTitleMarkup) : ""
  const titleText = typeof rawTitle === "string" ? rawTitle : ""
  const hasTitle = !!(titleMarkup.replace(/<[^>]*>/g, "").trim() || titleText)

  return (
    <OuterRoot data-block-type="vehicleFleetGallery" ref={entranceRef}>
      <Container paddingMobile>
        <Root style={transitionUpStyles(inView)}>
          {hasTitle && (
            <TitleRow>
              <ChevronWrapper>
                <GradientChevron width={16} height={27} />
              </ChevronWrapper>
              {titleMarkup ? (
                <Title
                  variant="h2"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: titleMarkup }}
                />
              ) : (
                <Title variant="h2">{titleText}</Title>
              )}
            </TitleRow>
          )}

          {/* EGDE-508: no scroll-button chevrons — on mobile the auto
              buttons ate row width and clipped the tab labels; the row
              still swipe-scrolls when it overflows. */}
          {showTabs && (
            <MuiTabs
              value={selectedCategory}
              onChange={(_, val) => handleCategoryChange(val)}
              variant="scrollable"
              scrollButtons={false}
            >
              {populatedCategories.map((cat) => (
                <MuiTab key={cat.key} label={cat.label} value={cat.key} />
              ))}
            </MuiTabs>
          )}
        </Root>
      </Container>

      {filteredImages.length > 0 && (
        <GallerySwiperContainer style={transitionUpStyles(inView, delayTime)}>
          <Swiper
            dir={isRtl ? "rtl" : "ltr"}
            modules={[FreeMode]}
            slidesPerView="auto"
            spaceBetween={16}
            freeMode={{ enabled: true, momentumRatio: 0.5 }}
            breakpoints={{ 1280: { spaceBetween: 24 } }}
          >
            {filteredImages.map((img, index) => {
              const slots = getSlots(img.media)
              return (
                <SwiperSlide key={img.id}>
                  <GalleryItem
                    onClick={() => handleOpenLightbox(index)}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      img.media.type === "youtubeVideo"
                        ? img.media.title || `Gallery video ${index + 1}`
                        : // EGDE-574: an uploaded video was announced as
                          // "Gallery image". This label opens the lightbox; the
                          // play/pause control inside has its own.
                          // EGDE-633: a card can hold a still for one breakpoint
                          // and a clip for the other; announce it as a video if
                          // either slot is one. Deliberately not breakpoint-
                          // dependent — that would differ between the server
                          // render and hydration.
                          img.media.alt ||
                          `Gallery ${
                            slots.desktop?.isVideo || slots.mobile?.isVideo
                              ? "video"
                              : "image"
                          } ${index + 1}`
                    }
                  >
                    {img.media.type === "youtubeVideo" ? (
                      <GalleryVideo>
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${extractVideoId(img.media.youtubeUrl)}?rel=0`}
                          title={img.media.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          loading="lazy"
                        />
                      </GalleryVideo>
                    ) : (
                      <>
                        {slots.desktop && (
                          <GalleryImage $hiddenOnMobile={!!slots.mobile}>
                            {renderAsset(
                              slots.desktop,
                              img.media.alt,
                              `${img.id}-desktop`,
                              "(max-width: 1279px) 300px, 384px",
                            )}
                          </GalleryImage>
                        )}
                        {/* EGDE-468: editor-provided mobile crop replaces the
                            desktop media below the desktop breakpoint.
                            EGDE-633: it may itself be a video. */}
                        {slots.mobile && (
                          <GalleryImageMobile>
                            {renderAsset(
                              slots.mobile,
                              img.media.alt,
                              `${img.id}-mobile`,
                              "300px",
                            )}
                          </GalleryImageMobile>
                        )}
                      </>
                    )}
                  </GalleryItem>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </GallerySwiperContainer>
      )}

      <Container paddingMobile>
        <Root style={transitionUpStyles(inView, delayTime * 2)}>
          {/* EGDE-424: the selected vehicle type's rich text. Hidden when empty.
              (EGDE-470: the block-level plain-text fallback was removed with
              its CMS field.) */}
          {descriptionMarkup && (
            <RichDescription
              dangerouslySetInnerHTML={{ __html: descriptionMarkup }}
            />
          )}

          {featureTags.length > 0 && (
            <FeaturesGrid>
              {featureTags.map((tag, index) => (
                <FeatureTag key={index}>
                  {tag.icon && (
                    <FeatureIcon>
                      <Image
                        src={tag.icon}
                        alt={tag.label}
                        width={20}
                        height={20}
                      />
                    </FeatureIcon>
                  )}
                  <FeatureLabel variant="body1">{tag.label}</FeatureLabel>
                </FeatureTag>
              ))}
            </FeaturesGrid>
          )}

          {(ctaLabel || priceLabel) && (
            <CtaSection>
              {(priceLabel || priceAmount) && (
                <PriceSection>
                  {priceLabel && (
                    <PriceLabel variant="body1">{priceLabel}</PriceLabel>
                  )}
                  {(pricePrefix || priceAmount) && (
                    <PriceRow>
                      {pricePrefix && (
                        <PricePrefix variant="body1">{pricePrefix}</PricePrefix>
                      )}
                      {priceAmount && (
                        <PriceAmount variant="body1">{priceAmount}</PriceAmount>
                      )}
                    </PriceRow>
                  )}
                </PriceSection>
              )}
              {ctaLabel &&
                ctaUrl &&
                (ctaLinkType === "Content" ? (
                  <CtaButton
                    href={ctaUrl}
                    target={ctaTarget}
                    rel={
                      ctaTarget === "_blank" ? "noopener noreferrer" : undefined
                    }
                  >
                    {ctaLabel}
                  </CtaButton>
                ) : (
                  <CtaButtonExternal
                    href={ctaUrl}
                    target={ctaTarget}
                    rel={
                      ctaTarget === "_blank" ? "noopener noreferrer" : undefined
                    }
                  >
                    {ctaLabel}
                  </CtaButtonExternal>
                ))}
            </CtaSection>
          )}
        </Root>
      </Container>

      {/* Lightbox dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: 0,
            overflow: "visible",
          },
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
          },
        }}
      >
        <LightboxContent>
          <LightboxCloseButton
            onClick={handleCloseLightbox}
            aria-label="Close lightbox"
          >
            &#x2715;
          </LightboxCloseButton>

          {currentLightboxImage?.media.type === "media" &&
            lightboxAsset &&
            (lightboxAsset.isVideo ? (
              <LightboxImageWrapper>
                <video
                  src={lightboxAsset.url}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </LightboxImageWrapper>
            ) : (
              <LightboxImageWrapper>
                <Image
                  src={lightboxAsset.url}
                  alt={currentLightboxImage.media.alt}
                  fill
                  sizes="90vw"
                  style={{ objectFit: "contain" }}
                  priority
                />
              </LightboxImageWrapper>
            ))}

          {currentLightboxImage?.media.type === "youtubeVideo" && (
            <LightboxImageWrapper>
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${extractVideoId(currentLightboxImage.media.youtubeUrl)}?rel=0&autoplay=1`}
                title={currentLightboxImage.media.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            </LightboxImageWrapper>
          )}

          {filteredImages.length > 1 && (
            <LightboxNav dir="ltr">
              <LightboxArrowButton
                onClick={handleLeftArrow}
                disabled={leftDisabled}
                aria-label={isRtl ? "Next" : "Previous"}
              >
                <ChevronLeft />
              </LightboxArrowButton>
              <LightboxCounter>
                {(lightboxIndex ?? 0) + 1}/{filteredImages.length}
              </LightboxCounter>
              <LightboxArrowButton
                onClick={handleRightArrow}
                disabled={rightDisabled}
                aria-label={isRtl ? "Previous" : "Next"}
              >
                <ChevronRight />
              </LightboxArrowButton>
            </LightboxNav>
          )}
        </LightboxContent>
      </Dialog>
    </OuterRoot>
  )
}
